import { useEffect, useMemo, useState } from "react";
import StageEditor from "../inbox/StageEditor";
import { LeadField, Stage, Tag, businessId, loadSetup, saveSetup, uid } from "../inbox/businessSetup";
import { saveLeadPipeline, getLeadPipeline } from "../../../api/leads.service";

const COLORS = ["#f97316", "#2563eb", "#7c3aed", "#16a34a", "#ef4444", "#64748b", "#0ea5e9", "#eab308"];

export default function PipelineSettings() {
  const [setup, setSetup] = useState(() => loadSetup());
  const [tags, setTags] = useState<Tag[]>(() => loadSetup().tags);
  const [fields, setFields] = useState<LeadField[]>(() => loadSetup().fields);
  const [name, setName] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    getLeadPipeline(businessId())
      .then((remote) => {
        if (cancelled || !remote) return;
        const stored = { ...loadSetup(), stages: remote.stages as Stage[], tags: remote.tags, fields: remote.fields };
        saveSetup(stored);
        setSetup(stored);
        setTags(stored.tags);
        setFields(stored.fields);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const persistPipeline = async (
    patch: { stages?: Stage[]; tags?: Tag[]; fields?: LeadField[] },
    ok: string
  ) => {
    const stored = { ...loadSetup(), ...patch };
    saveSetup(stored);
    setSetup(stored);
    if (patch.tags) setTags(patch.tags);
    if (patch.fields) setFields(patch.fields);
    try {
      await saveLeadPipeline({
        businessId: businessId(),
        stages: stored.stages,
        tags: stored.tags,
        fields: stored.fields,
      });
      setError("");
      setNote(ok);
    } catch {
      setNote("");
      setError("Could not save to the server.");
    }
  };

  const usedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(setup.leads).forEach((lead) => {
      if (!lead.stageId) return;
      counts[lead.stageId] = (counts[lead.stageId] || 0) + 1;
    });
    return counts;
  }, [setup.leads]);

  const addTag = () => {
    const label = name.trim();
    if (!label) {
      setError("Type a name for the tag.");
      return;
    }
    if (tags.some((tag) => tag.name.toLowerCase() === label.toLowerCase())) {
      setError("That tag name is already used.");
      return;
    }
    const next = tags.concat({ id: uid(), name: label, color });
    setName("");
    setColor(COLORS[next.length % COLORS.length]);
    setError("");
    void persistPipeline({ tags: next }, "Tags saved for this business.");
  };

  const removeTag = (id: string) => {
    const next = tags.filter((tag) => tag.id !== id);
    void persistPipeline({ tags: next }, "Tags saved for this business.");
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Stages and tags</h3>
        <p className="text-sm text-gray-600 mt-1">
          These belong to this business. Leads use them after you save. Another business keeps its own list.
        </p>
        {note ? <p className="mt-2 text-sm text-green-700">{note}</p> : null}
      </div>

      <StageEditor
        embedded
        stages={setup.stages}
        usedCounts={usedCounts}
        onClose={() => undefined}
        onSave={(stages) => {
          void persistPipeline({ stages }, "Stages saved for this business.");
        }}
      />

      <div className="border-t border-gray-100 pt-6">
        <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
        <p className="text-sm text-gray-600 mt-1">Create the tags for this business. They show on each lead row.</p>
        <div className="mt-4 space-y-2">
          {tags.length === 0 ? <p className="text-sm text-gray-500">No tags yet.</p> : null}
          {tags.map((tag) => (
            <div key={tag.id} className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ background: tag.color }} />
              <span className="text-sm font-medium text-gray-900">{tag.name}</span>
              <button type="button" onClick={() => removeTag(tag.id)} className="ml-auto text-sm text-red-600">
                Remove
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input
            type="color"
            value={color}
            aria-label="Tag color"
            onChange={(event) => setColor(event.target.value)}
            className="h-9 w-9 cursor-pointer rounded border border-gray-200 bg-white p-1"
          />
          <input
            value={name}
            aria-label="New tag name"
            placeholder="Tag name"
            onChange={(event) => setName(event.target.value)}
            className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
          <button type="button" onClick={addTag} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white">
            Add tag
          </button>
        </div>
        {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
      </div>

      <div className="border-t border-gray-100 pt-6">
        <h3 className="text-lg font-semibold text-gray-900">Fields</h3>
        <p className="text-sm text-gray-600 mt-1">Add a column only if this business wants one. The name you type becomes a new column on the leads table.</p>
        <div className="mt-4 space-y-2">
          {fields.length === 0 ? <p className="text-sm text-gray-500">No extra fields.</p> : null}
          {fields.map((field) => (
            <div key={field.id} className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{field.name}</span>
              <button
                type="button"
                onClick={() => {
                  const next = fields.filter((item) => item.id !== field.id);
                  void persistPipeline({ fields: next }, "Fields saved for this business.");
                }}
                className="ml-auto text-sm text-red-600"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input
            value={fieldName}
            aria-label="New field name"
            placeholder="Field name"
            onChange={(event) => setFieldName(event.target.value)}
            className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => {
              const label = fieldName.trim();
              if (!label) {
                setError("Type a name for the field.");
                return;
              }
              if (fields.some((field) => field.name.toLowerCase() === label.toLowerCase())) {
                setError("That field name is already used.");
                return;
              }
              const next = fields.concat({ id: uid(), name: label });
              setFieldName("");
              setError("");
              void persistPipeline({ fields: next }, "Fields saved for this business.");
            }}
            className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
          >
            Add field
          </button>
        </div>
      </div>
    </div>
  );
}
